/**
 * Operations analysis — monthly KPIs for piloting activity.
 *
 * Acquisition:
 *   KPI 1: New contacts this month (+ trend over last 6 months, delta vs previous)
 *   KPI 2: Contacts by acquisition channel (current month + previous)
 *
 * Activation:
 *   KPI 3: Ongoing tests / waiting tests (by dates)
 *   KPI 4: Tests starting soon / ending soon (next 2 weeks from Période location)
 *
 * Revenue:
 *   KPI 5: New installations this month (Clients with Date d'installation in current month)
 *   KPI 6: Concluded tests to follow up (Statut essai = Concluant but not yet a Client)
 *   KPI 7: Tests without status (need follow-up)
 */

const DATE_RE = /^\d{4}-\d{2}-\d{2}/;

function isDateLike(s) {
  return s && DATE_RE.test(s);
}

function monthLabel(ym) {
  const [y, m] = ym.split("-");
  const names = ["Jan", "Fév", "Mar", "Avr", "Mai", "Jun", "Jul", "Aoû", "Sep", "Oct", "Nov", "Déc"];
  return `${names[parseInt(m) - 1]} ${y}`;
}

function prevMonth(ym) {
  let [y, m] = ym.split("-").map(Number);
  m--;
  if (m < 1) { m = 12; y--; }
  return `${y}-${String(m).padStart(2, "0")}`;
}

function lastNMonths(currentYm, n) {
  const result = [];
  let ym = currentYm;
  for (let i = 0; i < n; i++) {
    result.unshift(ym);
    ym = prevMonth(ym);
  }
  return result;
}

function parseDate(s) {
  if (!isDateLike(s)) return null;
  return new Date(s.slice(0, 10));
}

function parsePeriode(s) {
  if (!s) return null;
  const parts = s.split(" - ");
  if (parts.length !== 2) return null;
  const start = parseDate(parts[0].trim());
  const end = parseDate(parts[1].trim());
  if (!start || !end) return null;
  return { start, end };
}

function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function formatDate(d) {
  return d.toISOString().slice(0, 10);
}

export function analyzeOperations({ contactsRows, contactsProRows, testsRows, clientsRows, referenceDate }) {
  const refDate = referenceDate || new Date();
  const currentYm = `${refDate.getFullYear()}-${String(refDate.getMonth() + 1).padStart(2, "0")}`;
  const prevYm = prevMonth(currentYm);
  const months = lastNMonths(currentYm, 6);

  // Collect all contacts with their first contact date and channel
  const contacts = [];

  for (const row of contactsRows || []) {
    const name = (row["Nom"] || "").trim();
    if (!name) continue;
    const dateStr = (row["Date de premier contact"] || "").trim();
    if (!isDateLike(dateStr)) continue;
    const channel = (row["Connu comment"] || "").trim() || "Non renseigné";
    contacts.push({ name, date: dateStr, month: dateStr.slice(0, 7), channel });
  }

  for (const row of contactsProRows || []) {
    const name = (row["Nom"] || "").trim();
    if (!name) continue;
    const dateStr = (row["Date de premier contact"] || "").trim();
    if (!isDateLike(dateStr)) continue;
    const channel = (row["Connu comment"] || "").trim() || "Non renseigné";
    contacts.push({ name, date: dateStr, month: dateStr.slice(0, 7), channel, isPro: true });
  }

  // --- KPI 1: New contacts per month ---

  const countByMonth = {};
  for (const c of contacts) {
    countByMonth[c.month] = (countByMonth[c.month] || 0) + 1;
  }

  const currentCount = countByMonth[currentYm] || 0;
  const prevCount = countByMonth[prevYm] || 0;
  const delta = currentCount - prevCount;

  const trendLabels = months.map(monthLabel);
  const trendData = months.map((m) => countByMonth[m] || 0);

  // --- KPI 2: Contacts by channel ---

  const currentContacts = contacts.filter((c) => c.month === currentYm);
  const prevContacts = contacts.filter((c) => c.month === prevYm);

  const channelsCurrent = {};
  for (const c of currentContacts) {
    channelsCurrent[c.channel] = (channelsCurrent[c.channel] || 0) + 1;
  }
  const channelsPrev = {};
  for (const c of prevContacts) {
    channelsPrev[c.channel] = (channelsPrev[c.channel] || 0) + 1;
  }

  // Merge all channel names, sorted by current count desc
  const allChannels = [...new Set([...Object.keys(channelsCurrent), ...Object.keys(channelsPrev)])];
  allChannels.sort((a, b) => (channelsCurrent[b] || 0) - (channelsCurrent[a] || 0));

  const channelLabels = allChannels;
  const channelCurrentData = allChannels.map((ch) => channelsCurrent[ch] || 0);
  const channelPrevData = allChannels.map((ch) => channelsPrev[ch] || 0);

  // --- Activation: Tests KPIs ---

  const today = new Date(refDate.getFullYear(), refDate.getMonth(), refDate.getDate());
  const twoWeeksOut = addDays(today, 14);

  const ongoing = [];
  const waiting = [];
  const startingSoon = [];
  const endingSoon = [];

  for (const row of testsRows || []) {
    const name = (row["Nom"] || "").trim();
    const periode = (row["Période location"] || "").trim();
    const demande = (row["Date de demande"] || "").trim();

    const parsed = parsePeriode(periode);

    if (parsed && parsed.start <= today && parsed.end >= today) {
      ongoing.push({ name, start: formatDate(parsed.start), end: formatDate(parsed.end) });
    } else if (isDateLike(demande) && (!parsed || parsed.start > today)) {
      waiting.push({ name, demande: demande.slice(0, 10), debut: parsed ? formatDate(parsed.start) : null });
    }

    if (parsed) {
      if (parsed.start >= today && parsed.start <= twoWeeksOut) {
        startingSoon.push({ name, date: formatDate(parsed.start) });
      }
      if (parsed.end >= today && parsed.end <= twoWeeksOut) {
        endingSoon.push({ name, date: formatDate(parsed.end) });
      }
    }
  }

  ongoing.sort((a, b) => a.end.localeCompare(b.end));
  waiting.sort((a, b) => a.demande.localeCompare(b.demande));

  startingSoon.sort((a, b) => a.date.localeCompare(b.date));
  endingSoon.sort((a, b) => a.date.localeCompare(b.date));

  // --- Revenue: Installations & concluded tests ---

  const EXCLUDED_GROUPS = new Set(["Wyes Internal Affairs"]);

  const clientNames = new Set();
  const newInstallations = [];
  const installByMonth = {};

  for (const row of clientsRows || []) {
    if (EXCLUDED_GROUPS.has((row["Groupe"] || "").trim())) continue;
    const name = (row["Nom"] || "").trim();
    if (!name) continue;
    clientNames.add(name.toLowerCase());
    const install = (row["Date d'installation"] || "").trim();
    if (!isDateLike(install)) continue;
    const ym = install.slice(0, 7);
    installByMonth[ym] = (installByMonth[ym] || 0) + 1;
    if (ym === currentYm) {
      newInstallations.push({ name, date: install.slice(0, 10) });
    }
  }

  newInstallations.sort((a, b) => a.date.localeCompare(b.date));

  const installCurrent = installByMonth[currentYm] || 0;
  const installPrev = installByMonth[prevYm] || 0;
  const installTrendData = months.map((m) => installByMonth[m] || 0);

  // Concluded tests not yet in Clients
  const concludedToContact = [];
  const noStatus = [];

  for (const row of testsRows || []) {
    const name = (row["Nom"] || "").trim();
    if (!name) continue;
    const statut = (row["Statut essai"] || "").trim();
    const periode = (row["Période location"] || "").trim();

    if (statut === "Concluant" && !clientNames.has(name.toLowerCase())) {
      concludedToContact.push({ name, periode });
    }
    if (!statut) {
      const parsed = parsePeriode(periode);
      // Only flag tests whose period has ended (not ongoing/future)
      if (parsed && parsed.end < today) {
        noStatus.push({ name, end: formatDate(parsed.end) });
      }
    }
  }

  noStatus.sort((a, b) => a.end.localeCompare(b.end));

  return {
    currentMonth: monthLabel(currentYm),
    prevMonth: monthLabel(prevYm),
    newContacts: {
      current: currentCount,
      previous: prevCount,
      delta,
      trendLabels,
      trendData,
    },
    channels: {
      labels: channelLabels,
      currentData: channelCurrentData,
      prevData: channelPrevData,
      currentMonth: monthLabel(currentYm),
      prevMonth: monthLabel(prevYm),
    },
    activation: {
      ongoing,
      waiting,
      startingSoon,
      endingSoon,
    },
    revenue: {
      installCurrent,
      installPrev,
      installDelta: installCurrent - installPrev,
      installTrendLabels: months.map(monthLabel),
      installTrendData,
      newInstallations,
      concludedToContact,
      noStatus,
    },
  };
}
